import React from 'react';

import cx from 'classnames';

import { FocusIndicator } from '../FocusIndicator/FocusIndicator';

interface ChipButtonProps
  extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    'type' | 'checked'
  > {
  children: React.ReactNode;
  /** Whether the chip is in a selected state. */
  isSelected?: boolean;
}

export const ChipButton = React.forwardRef<
  HTMLInputElement | null,
  ChipButtonProps
>(({ children, isSelected, className, ...rest }, ref) => {
  const labelClass = React.useMemo(
    () =>
      cx(
        'group relative m-0 px-4 py-4px',
        'text-xs font-600 leading-8',
        'border-2 border-solid rounded-pill select-none inline-block',
        'transition ease-in-out duration-150',
        rest.disabled
          ? 'text-disabled bg-grey-50 border-grey-200 cursor-not-allowed'
          : [
              'cursor-pointer',
              isSelected
                ? 'bg-cyan-50 text-cyan-700 border-cyan-200'
                : 'bg-white text-grey-900 border-grey-200 hover:bg-grey-50',
            ],
        className
      ),
    [rest.disabled, isSelected, className]
  );

  return (
    <label className={labelClass} data-testid="chip-button-label">
      <input
        ref={ref}
        type="checkbox"
        checked={isSelected}
        className="sr-only"
        {...rest}
      />
      <FocusIndicator colorClass="text-cyan-700" />
      {children}
    </label>
  );
});

ChipButton.displayName = 'ChipButton';
